/**
 * And Then Some: point the product page's links-to-reviews at the reviews
 * block on the same page, when there is one.
 *
 * Two links use this: the buy column's store-rating line
 * (.ats-pdp__rating-link) and the in-column highlighted reviews widget's
 * "Read all reviews" footer (.ats-pdp-hl__all).
 *
 * ⚠️ THE FALLBACK IS THE SERVED MARKUP, NOT A BRANCH IN HERE, AND THAT IS THE
 * WHOLE DESIGN. sections/ats-main-product.liquid always renders the link as
 * `/pages/reviews`, which is correct on its own and identical on every product
 * page: the store-rating line is a store signal, so its bytes must not vary by
 * product. This file is loaded ONLY by sections/ats-pdp-reviews.liquid, and
 * that section only renders when the ats.reviews metafield actually holds
 * reviews. So:
 *
 *   block present  -> this runs, finds the target, upgrades the link
 *   block absent   -> this file is never on the page, link stays /pages/reviews
 *   no JavaScript  -> link stays /pages/reviews
 *
 * There is no state in which the link points at an anchor that does not exist.
 * It is gated by the presence of the thing it points at, which is stronger than
 * any check this file could make about itself.
 *
 * ⚠️ THE ANCHOR IS NOT WRITTEN INTO THE HREF BY LIQUID, deliberately. Liquid
 * cannot see whether another section is on the template, so the product section
 * would have had to guess. Guessing wrong in one direction gives every product
 * page a link to a missing anchor, which silently does nothing when clicked.
 */
(function () {
  'use strict';

  var TARGET_ID = 'ats-pdp-reviews';

  /* ⚠️ TWO LINKS NOW, AND THEY SHARE EVERY WORD OF THE REASONING ABOVE. The
     buy column's store-rating line was the first; the in-column highlighted
     reviews widget's "Read all reviews" footer link is the second. Both serve
     /pages/reviews in the markup and both are upgraded here, so both inherit
     the same gate (this file only exists on a page that has the target), the
     same smooth scroll, the same reduced-motion handling and the same settle
     correction. Adding the second link is one selector rather than a second
     implementation, which is the point of upgrading them from one place. */
  var LINK = '.ats-pdp__rating-link, .ats-pdp-hl__all';

  function scroller() {
    /* Above 990px Horizon scrolls .page-wrapper, not the window, the same thing
       assets/scroll-container.js and every measurement in this build has to
       account for. scrollIntoView finds it by itself; the settle correction
       below has to be told. */
    var wrap = document.querySelector('.page-wrapper');
    if (wrap && wrap.scrollHeight > wrap.clientHeight) return wrap;
    return null;
  }

  /* ⚠️ scrollIntoView, NOT A COMPUTED scrollTo, AND THE FIRST VERSION GOT THIS
     WRONG IN A WAY ONLY MEASUREMENT CAUGHT. Computing a target offset once and
     handing it to scrollTo({behavior:'smooth'}) assumes the page does not move
     during the animation. It does: images below the fold finish loading while
     the scroll is in flight, so the destination drifts. Measured on a 1440
     viewport, the smooth path landed the heading at 58px against a header
     bottom of 64 (SIX PIXELS UNDER the header, so the heading was clipped)
     while the identical instant path landed correctly at 88. A 30px error that
     appears only when animating is exactly the sort a static check misses.

     scrollIntoView recomputes as it goes, honours the element's own
     scroll-margin-top, and picks the right scroller by itself, which also
     removes the .page-wrapper special case this file used to carry.

     The heading carries its own scroll-margin-top in the section's stylesheet;
     the section keeps a separate one for native fragment jumps. */
  function go(event) {
    var target = document.getElementById(TARGET_ID);
    if (!target) return; /* Let the href do its job. */

    event.preventDefault();

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var anchor = target.querySelector('.rv-head h2') || target;

    anchor.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

    /* ⚠️ A SMOOTH scrollIntoView COMPUTES ITS DESTINATION ONCE AND DOES NOT
       RE-TARGET, so anything that changes height while the scroll is in flight
       leaves it short or long. On this page that is guaranteed rather than
       unlikely: the journey passes the recommendation rails, whose
       IntersectionObserver fetch fires as they enter view and swaps their
       contents in. Measured before this correction: the instant path landed the
       heading 24px below the header exactly as intended, while the identical
       smooth path finished at MINUS 6, six pixels underneath it, every time and
       at both widths.

       So the landing is re-checked once the scroll settles and nudged if it is
       off. The nudge is instant, because a second animation to fix the first
       reads as a bounce, and it only fires beyond a 2px tolerance so sub-pixel
       rounding never produces a visible twitch. */
    if (!reduce) {
      var settle = function () {
        var want = parseFloat(getComputedStyle(anchor).scrollMarginTop);
        if (isNaN(want)) want = 88;
        var off = anchor.getBoundingClientRect().top - want;
        if (Math.abs(off) > 2) {
          var sc = scroller();
          if (sc) sc.scrollTo({ top: sc.scrollTop + off, behavior: 'auto' });
          else window.scrollTo({ top: window.pageYOffset + off, behavior: 'auto' });
        }
      };
      /* ⚠️ LISTEN ON THE SCROLLER, NOT ON window, AND THAT DISTINCTION IS THE
         WHOLE FIX. Above 990px the thing that scrolls is .page-wrapper, so a
         `scrollend` bound to window never fires there and only the timer ran,
         mid-animation, before the scroll had finished. Measured: with the
         listener on window the 390 case corrected properly (window scrolls) and
         the 1440 case did not (page-wrapper scrolls), landing 6px under the
         header. Same code, opposite results, purely because of which element
         owns the scroll.

         Belt and braces after that: scrollend where supported, plus two timed
         passes, because a correction that misses is the bug this exists for and
         re-running it when it is already correct costs nothing (the 2px
         tolerance makes a no-op cheap and invisible). */
      var target_scroller = scroller() || window;
      if ('onscrollend' in window) {
        target_scroller.addEventListener('scrollend', settle, { once: true });
      }
      window.setTimeout(settle, 700);
      window.setTimeout(settle, 1400);
    }

    /* The hash is set after, and with replaceState rather than by assigning
       location.hash, so the browser never fires its own instant jump against
       the smooth one. */
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', '#' + TARGET_ID);
    }

    /* Focus for keyboard and screen reader users: scrolling moves the viewport
       and leaves the reading position behind. preventScroll because the scroll
       above is already doing it, and tabindex -1 makes a non interactive
       section focusable without adding it to the tab order. */
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function boot() {
    if (!document.getElementById(TARGET_ID)) return;
    var links = document.querySelectorAll(LINK);
    for (var i = 0; i < links.length; i++) {
      links[i].setAttribute('href', '#' + TARGET_ID);
      links[i].addEventListener('click', go);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
